// Error: 'React' was used before it was defined  @typescript-eslint/no-use-before-define
// So use: // eslint-disable-next-line
// This issule just begin (14/09/2020)
// https://github.com/typescript-eslint/typescript-eslint/issues/2540

// Import package from NPM
// eslint-disable-next-line
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Layout } from 'antd';

// Import custom by Template
import SidebarContent from './SidebarContent';
import { toggleCollapsedSideNav, updateWindowWidth } from 'appRedux/actions';
import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  TAB_SIZE,
  THEME_TYPE_LITE,
} from '../../constants/ThemeSetting';

// Import custom by Project
import { AppState } from 'ReducerState';
import { useResponsive } from 'components/responsive';

const { Sider } = Layout;

const Sidebar = () => {
  const dispatch = useDispatch();
  const { device } = useResponsive();
  const isIPad = device === 'ipad';

  const { themeType, navStyle } = useSelector((state: AppState) => state.settings);

  const { navCollapsed, width } = useSelector((state: AppState) => state.common);
  const shouldUseMobileNav = width < TAB_SIZE || isIPad;

  const onToggleCollapsedNav = () => {
    dispatch(toggleCollapsedSideNav(!navCollapsed));
  };

  useEffect(() => {
    const handleResize = () => {
      dispatch(updateWindowWidth(window.innerWidth));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [dispatch]);

  // Auto-expand sidebar when switching from mobile to desktop
  // This ensures sidebar is always visible on desktop screens for smooth UX
  useEffect(() => {
    const isNowDesktop = !shouldUseMobileNav;
    const isDesktopWidth = width >= TAB_SIZE && !isIPad;

    // When switching from mobile to desktop mode
    // Auto-expand sidebar to ensure it's always visible on desktop
    if (isNowDesktop && isDesktopWidth) {
      // On desktop, sidebar should be visible - auto-expand if collapsed
      // This handles the case when user was on mobile, collapsed sidebar, then resized to desktop
      if (navCollapsed) {
        dispatch(toggleCollapsedSideNav(false));
      }
    }
  }, [shouldUseMobileNav, navCollapsed, dispatch, width, isIPad]);

  // Calculate effective navStyle: if DRAWER on desktop, treat as FIXED
  // This ensures sidebar shows immediately when resizing from mobile to desktop
  const effectiveNavStyle =
    navStyle === NAV_STYLE_DRAWER && !shouldUseMobileNav && width >= TAB_SIZE
      ? NAV_STYLE_FIXED
      : navStyle;

  // Determine drawer style based on navStyle and screen size
  // Priority: Mobile detection first, then navStyle
  let drawerStyle = 'gx-collapsed-sidebar';

  // On mobile/tablet, always use collapsed sidebar (Drawer mode)
  if (shouldUseMobileNav) {
    // Mobile mode - sidebar is hidden, shown via Drawer
    drawerStyle = 'gx-collapsed-sidebar';
  } else {
    // Desktop mode - sidebar should be visible based on effectiveNavStyle

    if (effectiveNavStyle === NAV_STYLE_FIXED) {
      drawerStyle = '';
    } else if (effectiveNavStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR) {
      drawerStyle = 'gx-mini-sidebar gx-mini-custom-sidebar';
    } else if (effectiveNavStyle === NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR) {
      drawerStyle = 'gx-custom-sidebar';
    } else if (effectiveNavStyle === NAV_STYLE_MINI_SIDEBAR) {
      drawerStyle = 'gx-mini-sidebar';
    } else {
      // Fallback: show sidebar anyway on desktop (better than blank blue area)
      // This prevents the bug where sidebar shows blue background but no content
      drawerStyle = '';
    }
  }
  return (
    <Sider
      className={`gx-app-sidebar ${drawerStyle} ${themeType !== THEME_TYPE_LITE ? 'gx-layout-sider-dark h-full' : null}`}
      trigger={null}
      collapsed={
        shouldUseMobileNav
          ? false
          : effectiveNavStyle === NAV_STYLE_MINI_SIDEBAR ||
            effectiveNavStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR
      }
      theme={themeType === THEME_TYPE_LITE ? 'light' : 'dark'}
      collapsible
      width={shouldUseMobileNav ? '70vw' : 256}
      style={shouldUseMobileNav ? { maxWidth: '300px' } : undefined}
    >
      {
        // Only use Drawer component on mobile/tablet, NOT on desktop
        // On desktop, always render SidebarContent directly in Sider to prevent blank blue area
        shouldUseMobileNav ? (
          <Drawer
            className={`gx-drawer-sidebar ${themeType !== THEME_TYPE_LITE ? 'gx-drawer-sidebar-dark' : null}`}
            placement='left'
            closable={false}
            onClose={onToggleCollapsedNav}
            open={navCollapsed}
            width='min(70vw, 300px)'
            styles={{
              body: { padding: 0, backgroundColor: '#003366' },
              wrapper: { boxShadow: 'none' },
            }}
            zIndex={10001}
          >
            <SidebarContent />
          </Drawer>
        ) : (
          <SidebarContent />
        )
      }
    </Sider>
  );
};

export default Sidebar;
